/**
 * Leverantörsregister för dokumentdestinationer.
 *
 * Registret är den enda plats där en ny plattform behöver läggas till – UI:t
 * läser alltid härifrån. Systemet hanterar aldrig användarens lösenord eller
 * SMS-kod: tvåstegsverifieringen sker alltid hos plattformen själv.
 */

export type AuthMode = "oauth" | "service" | "api_key";

export type ConnectionStatus = "pending" | "connected" | "needs_reauth" | "disconnected";

export interface ProviderField {
  key: string;
  label: string;
  placeholder?: string;
  /** true = värdet är en hemlighet och lagras enbart serversidan. */
  secret?: boolean;
  required?: boolean;
  help?: string;
}

export interface ProviderDefinition {
  id: string;
  name: string;
  /** Kort beskrivning som visas i kopplingsvyn. */
  description: string;
  authModes: AuthMode[];
  /** Vilken edge function som hanterar kopplingen. */
  handler: "microsoft-destination" | "generic-destination";
  /** Fält som användaren fyller i vid nyckel-/tjänstekoppling. */
  fields?: ProviderField[];
  /** Stöder plattformen att systemet skapar mappträd? */
  supportsFolderTree: boolean;
  /** Är kopplingen färdig att använda idag? */
  available: boolean;
}

export const PROVIDERS: ProviderDefinition[] = [
  {
    id: "sharepoint",
    name: "SharePoint",
    description:
      "Dokumenten hamnar i organisationens SharePoint-sajt. Användaren loggar in en gång med sin vanliga Microsoft-inloggning inklusive tvåstegsverifiering.",
    authModes: ["oauth", "service"],
    handler: "microsoft-destination",
    supportsFolderTree: true,
    available: true,
  },
  {
    id: "onedrive",
    name: "OneDrive",
    description:
      "Personlig eller delad OneDrive-mapp. Samma inloggning och tvåstegsverifiering som i övriga Microsoft 365.",
    authModes: ["oauth"],
    handler: "microsoft-destination",
    supportsFolderTree: true,
    available: true,
  },
  {
    id: "dalux",
    name: "Dalux",
    description:
      "Publicera arbetsmiljödokument till Dalux Field/Build. Kopplas med en API-nyckel som organisationens administratör hämtar i Dalux.",
    authModes: ["api_key", "service"],
    handler: "generic-destination",
    supportsFolderTree: false,
    available: true,
    fields: [
      { key: "base_url", label: "API-adress", placeholder: "https://api.dalux.com", required: true },
      { key: "project_ref", label: "Projekt-ID i Dalux", required: true },
      { key: "api_key", label: "API-nyckel", secret: true, required: true, help: "Lagras krypterat och visas aldrig igen." },
    ],
  },
  {
    id: "interaxo",
    name: "Interaxo",
    description: "Skicka dokument till ett Interaxo-rum via organisationens API-nyckel.",
    authModes: ["api_key"],
    handler: "generic-destination",
    supportsFolderTree: false,
    available: true,
    fields: [
      { key: "base_url", label: "API-adress", required: true },
      { key: "project_ref", label: "Rum/mapp-ID", required: true },
      { key: "api_key", label: "API-nyckel", secret: true, required: true },
    ],
  },
  {
    id: "webhook",
    name: "Egen mottagare (webhook)",
    description:
      "För egna dokumenthanteringssystem. Varje dokument skickas som en signerad HTTPS-förfrågan till en adress ni anger.",
    authModes: ["api_key"],
    handler: "generic-destination",
    supportsFolderTree: false,
    available: true,
    fields: [
      { key: "base_url", label: "Mottagaradress (https)", placeholder: "https://…", required: true },
      { key: "api_key", label: "Delad hemlighet", secret: true, required: true, help: "Används för att signera anropen." },
    ],
  },
  {
    id: "google_drive",
    name: "Google Drive",
    description: "Planerad koppling med samma eng\u00e5ngsinloggning som Microsoft.",
    authModes: ["oauth"],
    handler: "generic-destination",
    supportsFolderTree: true,
    available: false,
  },
  {
    id: "box",
    name: "Box",
    description: "Planerad koppling.",
    authModes: ["oauth"],
    handler: "generic-destination",
    supportsFolderTree: true,
    available: false,
  },
];

export const getProvider = (id: string | null | undefined): ProviderDefinition =>
  PROVIDERS.find((p) => p.id === id) ?? PROVIDERS[0];

export const AUTH_MODE_LABEL: Record<AuthMode, string> = {
  oauth: "Personlig inloggning",
  service: "Organisationskoppling",
  api_key: "API-nyckel",
};

export const AUTH_MODE_HELP: Record<AuthMode, string> = {
  oauth:
    "Du loggar in hos plattformen en gång och klarar din tvåstegsverifiering där. Vi får bara en åtkomstnyckel – aldrig ditt lösenord.",
  service:
    "IT-avdelningen godkänner systemet en gång för hela organisationen. Kopplingen fungerar oberoende av enskilda användare.",
  api_key:
    "För plattformar utan inloggningsflöde. Nyckeln lagras krypterat på servern och visas aldrig igen efter att den sparats.",
};

export const STATUS_LABEL: Record<ConnectionStatus, string> = {
  pending: "Ej ansluten",
  connected: "Aktiv",
  needs_reauth: "Behöver förnyas",
  disconnected: "Frånkopplad",
};

export const STATUS_HELP: Record<ConnectionStatus, string> = {
  pending: "Kopplingen är skapad men ingen har anslutit än.",
  connected: "Dokument kan skickas. Åtkomsten förnyas automatiskt.",
  needs_reauth: "Åtkomsten har gått ut. Klicka på Anslut för att logga in igen.",
  disconnected: "Kopplingen är avstängd och används inte.",
};
